import { Injectable, Injector } from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { environment } from 'src/environments/environment';
import { StorageService } from 'src/app/_services/storage.service';
import { AuthService } from 'src/app/account/auth/_services/auth.service';

/** Marks a request already retried after a renewal, so it is never renewed twice. */
const RETRIED_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);

/**
 * Renews the session token when a request sent with it gets a 401, then retries the
 * request once with the new token. Concurrent 401s share one renewal; if the renewal
 * is rejected the session is closed and the user is sent to the login screen.
 * Must be registered after LockerAuthInterceptor so it sees the bearer that one adds.
 */
@Injectable()
export class SessionRefreshInterceptor implements HttpInterceptor {
  private readonly http: HttpClient;
  private refreshInFlight$: Observable<string> | null = null;
  private replacedToken: string | null = null;

  constructor(
    httpBackend: HttpBackend,
    private storageService: StorageService,
    private injector: Injector,
  ) {
    // Bypasses the interceptor chain so the renewal call is never intercepted itself.
    this.http = new HttpClient(httpBackend);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error) => {
        if (!this.isRenewable(req, error)) {
          return throwError(error);
        }
        return this.currentOrRenewedToken(req).pipe(
          switchMap((token) => next.handle(req.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
            context: req.context.set(RETRIED_AFTER_REFRESH, true),
          }))),
        );
      }),
    );
  }

  private isRenewable(req: HttpRequest<any>, error: any): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 401) return false;
    if (req.context.get(RETRIED_AFTER_REFRESH)) return false;
    const sentToken = this.bearerOf(req);
    return !!sentToken && (sentToken === this.sessionToken() || sentToken === this.replacedToken);
  }

  /** A request sent with a token already replaced by a renewal just uses the current one. */
  private currentOrRenewedToken(req: HttpRequest<any>): Observable<string> {
    const current = this.sessionToken();
    if (current && this.bearerOf(req) === this.replacedToken && current !== this.replacedToken) {
      return new Observable<string>((subscriber) => {
        subscriber.next(current);
        subscriber.complete();
      });
    }
    return this.renewToken();
  }

  private renewToken(): Observable<string> {
    if (!this.refreshInFlight$) {
      const session = this.storageService.getItem('currentUser');
      this.refreshInFlight$ = this.http
        .post<{ token: string }>(`${environment.microservices.user}auth/refresh-token`, { token: session?.token })
        .pipe(
          map((res) => {
            if (!res?.token) throw new Error('Refresh response without token');
            this.replacedToken = session?.token ?? null;
            this.storageService.setItem('currentUser', { ...session, token: res.token });
            return res.token;
          }),
          catchError((error) => {
            this.expireSession();
            return throwError(error);
          }),
          finalize(() => (this.refreshInFlight$ = null)),
          shareReplay(1),
        );
    }
    return this.refreshInFlight$;
  }

  private expireSession(): void {
    this.injector.get(AuthService).logout();
    Swal.fire({
      icon: 'info',
      title: 'Tu sesión expiró',
      text: 'Por favor, vuelve a iniciar sesión.',
    });
  }

  private sessionToken(): string | null {
    return this.storageService.getItem('currentUser')?.token ?? null;
  }

  private bearerOf(req: HttpRequest<any>): string | null {
    const header = req.headers.get('Authorization');
    return header?.startsWith('Bearer ') ? header.slice('Bearer '.length) : null;
  }
}
